import {useState} from 'react';
import styled, {css, keyframes} from 'styled-components';
import {FELT, FeltDefs, useFeltIds} from './felt';
import {Sparkle} from './feltFriends';
import {
  chipSpots,
  chipsInRightHalf,
  COOKIE_THETA,
  cookieChart,
  cookiesPerPlainOne,
  sampleChipCount,
  type ChipSpot,
} from './cookieChips';
import {ToyShell, type MapToyProps} from './mapToys';
import {playTones, type Tone} from './toySounds';

/**
 * The cookie oven: Mehran's chocolate chips. A press bakes a cookie and
 * its chips land one by one, Poisson(θ) over the whole cookie. The next
 * press takes a bite: half the cookie goes, and the chips it held are
 * Poisson(θ/2). The press after that bakes the next cookie, and a small
 * chart above the plate fills in with chips per cookie beside Poisson(θ).
 * Once in about e^θ cookies one comes out plain, with no chips at all.
 */

/** The scene's viewBox, in units. */
const VIEW = {width: 120, height: 96} as const;

/** The cookie's centre and radius, in viewBox units. */
const COOKIE = {cx: 92, cy: 66, r: 16} as const;

/** Where the chart sits, in viewBox units. */
const CHART = {left: 70, right: 116, top: 6, base: 34} as const;

/** How long a chip takes to land after the one before, in seconds. */
const CHIP_GAP_SEC = 0.11;

/** The oven's three states for the cookie on the plate. */
type Stage = 'cold' | 'baked' | 'bitten';

const reducedMotion = css`
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const slideOut = keyframes`
  0% { transform: translate(-30px, 0) scale(0.6); opacity: 0; }
  60% { transform: translate(3px, 0) scale(1.04); opacity: 1; }
  100% { transform: translate(0, 0) scale(1); opacity: 1; }
`;

/** The cookie sliding from the oven onto the plate when it mounts. */
const FreshCookie = styled.g`
  transform-box: fill-box;
  transform-origin: center;
  animation: ${slideOut} 0.6s ease-out;
  ${reducedMotion}
`;

const land = keyframes`
  0% { transform: translateY(-14px) scale(0.4); opacity: 0; }
  70% { transform: translateY(1px) scale(1.15); opacity: 1; }
  100% { transform: translateY(0) scale(1); opacity: 1; }
`;

/** A chip dropping onto the cookie, after its own delay. */
const Chip = styled.g<{$delaySec: number}>`
  transform-box: fill-box;
  transform-origin: center;
  opacity: 0;
  animation: ${land} 0.35s ease-out ${({$delaySec}) => $delaySec}s forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }
`;

const glow = keyframes`
  0%, 100% { opacity: 0.55; }
  50% { opacity: 0.95; }
`;

/** The oven window's warm light while the oven is on. */
const Warmth = styled.ellipse`
  animation: ${glow} 1.4s ease-in-out infinite;
  ${reducedMotion}
`;

/** A soft two-note ding when the cookie is done. */
const DING: readonly Tone[] = [
  {frequency: 1318.5, startSec: 0, durationSec: 0.5},
  {frequency: 1760, startSec: 0.12, durationSec: 0.8},
];

/** Two low thuds for a bite. */
const CRUNCH: readonly Tone[] = [
  {frequency: 160, startSec: 0, durationSec: 0.08},
  {frequency: 120, startSec: 0.07, durationSec: 0.1},
];

/** The oven's body, door and dials. */
function Oven({isOn}: {isOn: boolean}) {
  return (
    <g>
      <rect x={6} y={32} width={58} height={58} rx={6} fill="#D9D4CC" />
      <rect x={6} y={32} width={58} height={10} rx={5} fill="#C4BEB5" />
      <circle cx={16} cy={37} r={2.4} fill={FELT.ink} />
      <circle cx={25} cy={37} r={2.4} fill={FELT.ink} />
      <rect x={38} y={35} width={20} height={4} rx={2} fill="#8A847B" />
      <rect x={12} y={46} width={46} height={38} rx={4} fill="#6B655E" />
      <rect x={18} y={52} width={34} height={22} rx={3} fill="#2A2522" />
      {isOn && (
        <Warmth cx={35} cy={64} rx={15} ry={9} fill="#F2A33A" opacity={0.7} />
      )}
      <rect x={16} y={78} width={38} height={2.4} rx={1.2} fill="#8A847B" />
      <ellipse
        cx={35}
        cy={90}
        rx={30}
        ry={2.4}
        fill={FELT.ink}
        opacity={0.12}
      />
    </g>
  );
}

/** The plate the cookie lands on. */
function Plate() {
  return (
    <g>
      <ellipse cx={COOKIE.cx} cy={84} rx={25} ry={7} fill="#EEF0F2" />
      <ellipse cx={COOKIE.cx} cy={83.4} rx={21} ry={5} fill="#FFFFFF" />
    </g>
  );
}

/**
 * The cookie with its chips. Bitten, the right half is gone, leaving a
 * scalloped edge and a few crumbs.
 */
function Cookie({
  spots,
  isBitten,
  clipId,
}: {
  spots: readonly ChipSpot[];
  isBitten: boolean;
  clipId: string;
}) {
  const {cx, cy, r} = COOKIE;
  const bite = `M ${cx} ${cy - r - 1} C ${cx - 2} ${cy - 10} ${cx + 3} ${cy - 6} ${cx - 1} ${cy - 2} C ${cx + 3} ${cy + 1} ${cx - 2} ${cy + 6} ${cx + 1} ${cy + r + 1} L ${cx - r - 2} ${cy + r + 2} L ${cx - r - 2} ${cy - r - 2} Z`;
  return (
    <g>
      <clipPath id={clipId}>
        {isBitten ? <path d={bite} /> : <circle cx={cx} cy={cy} r={r + 2} />}
      </clipPath>
      <g clipPath={`url(#${clipId})`}>
        <circle cx={cx} cy={cy} r={r} fill="#C98A4B" />
        <circle cx={cx - 2} cy={cy - 2} r={r - 3} fill="#D9A062" />
        {spots.map((spot, index) => (
          <Chip
            key={`${spot.x.toFixed(3)}-${spot.y.toFixed(3)}`}
            $delaySec={index * CHIP_GAP_SEC}
          >
            <ellipse
              cx={cx + spot.x * (r - 1.6)}
              cy={cy + spot.y * (r - 1.6)}
              rx={1.8}
              ry={1.5}
              fill="#4A2C1A"
            />
          </Chip>
        ))}
      </g>
      {isBitten && (
        <g fill="#C98A4B">
          <circle cx={cx + 9} cy={cy + 14} r={1} />
          <circle cx={cx + 14} cy={cy + 10} r={0.8} />
          <circle cx={cx + 6} cy={cy + 17} r={0.7} />
        </g>
      )}
    </g>
  );
}

/** The chips-per-cookie chart beside Poisson(θ), without words. */
function Chart({cookies}: {cookies: readonly number[]}) {
  const bins = cookieChart({cookies, theta: COOKIE_THETA});
  const slot = (CHART.right - CHART.left) / bins.length;
  const tallest = Math.max(
    0.0001,
    ...bins.map(bin => Math.max(bin.observed, bin.expected)),
  );
  const height = CHART.base - CHART.top;
  const dots = bins
    .map((bin, index) => {
      const x = CHART.left + (bin.k + 0.5) * slot;
      const y = CHART.base - (bin.expected / tallest) * height;
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <g>
      <path
        d={`M ${CHART.left} ${CHART.base} H ${CHART.right}`}
        stroke="#A8A29E"
        strokeWidth={0.6}
      />
      {bins.map(bin => (
        <rect
          key={bin.k}
          x={CHART.left + bin.k * slot + slot * 0.2}
          y={CHART.base - (bin.observed / tallest) * height}
          width={slot * 0.6}
          height={(bin.observed / tallest) * height}
          fill="#5A6FD1"
          opacity={0.8}
        />
      ))}
      <path d={dots} fill="none" stroke={FELT.cardinal} strokeWidth={0.5} />
      {bins.map(bin => (
        <circle
          key={bin.k}
          cx={CHART.left + (bin.k + 0.5) * slot}
          cy={CHART.base - (bin.expected / tallest) * height}
          r={0.8}
          fill={FELT.cardinal}
        />
      ))}
    </g>
  );
}

/** The scene's caption for its current stage. */
function captionFor({
  stage,
  spots,
  cookies,
}: {
  stage: Stage;
  spots: readonly ChipSpot[];
  cookies: readonly number[];
}): string {
  const chips = spots.length;
  if (stage === 'cold') {
    return `an oven; press to bake a cookie with Poisson chips, θ = ${COOKIE_THETA} chips a cookie on average`;
  }
  if (stage === 'baked') {
    if (chips === 0) {
      return `a plain cookie, not one chip! that happens about once in ${cookiesPerPlainOne(COOKIE_THETA)} cookies; press to bake another`;
    }
    const sofar =
      cookies.length > 1
        ? `; ${cookies.length} cookies so far, ${(cookies.reduce((a, b) => a + b, 0) / cookies.length).toFixed(1)} chips a cookie on average`
        : '';
    return `cookie ${cookies.length}: ${chips} ${chips === 1 ? 'chip' : 'chips'} landed, θ = ${COOKIE_THETA} on average${sofar}; press to take a bite`;
  }
  const bitten = chipsInRightHalf(spots);
  return `the bite took ${bitten} of the ${chips} chips; half a cookie holds Poisson θ/2 = ${COOKIE_THETA / 2} chips on average; press to bake another`;
}

/**
 * The cookie oven toy. Press to bake a cookie, press to bite it, press to
 * bake the next.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyCookieOven({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [stage, setStage] = useState<Stage>('cold');
  const [spots, setSpots] = useState<ChipSpot[]>([]);
  const [cookies, setCookies] = useState<number[]>([]);

  const bake = () => {
    const count = sampleChipCount({theta: COOKIE_THETA, random: Math.random});
    const next = chipSpots({count, random: Math.random});
    setSpots(next);
    setCookies(previous => [...previous, count]);
    setStage('baked');
    playTones({
      tones: [
        ...DING,
        ...next.map((_, index) => ({
          frequency: 2200,
          startSec: 0.5 + index * CHIP_GAP_SEC,
          durationSec: 0.05,
        })),
      ],
      wave: 'triangle',
    });
  };

  const poke = () => {
    if (stage === 'baked' && spots.length > 0) {
      setStage('bitten');
      playTones({tones: CRUNCH, wave: 'triangle'});
      return;
    }
    bake();
  };

  const previewSpots: ChipSpot[] = [
    {x: -0.4, y: -0.3},
    {x: 0.3, y: -0.5},
    {x: 0.1, y: 0.2},
    {x: -0.5, y: 0.4},
    {x: 0.5, y: 0.4},
  ];
  const shown = isPreview ? previewSpots : spots;
  const hasCookie = isPreview || stage !== 'cold';

  return (
    <ToyShell
      isPreview={isPreview}
      label="Cookie oven"
      caption={captionFor({stage, spots, cookies})}
      onPoke={poke}
    >
      <svg
        viewBox={`0 0 ${VIEW.width} ${VIEW.height}`}
        width={size}
        height={(size * VIEW.height) / VIEW.width}
        overflow="visible"
        aria-hidden
      >
        <defs>
          <FeltDefs ids={ids} />
        </defs>
        <Oven isOn={stage === 'baked'} />
        {!isPreview && <Chart cookies={cookies} />}
        <Plate />
        {hasCookie && (
          <FreshCookie key={cookies.length}>
            <Cookie
              spots={shown}
              isBitten={stage === 'bitten'}
              clipId={`${ids.fuzz}-cookie`}
            />
          </FreshCookie>
        )}
        {stage === 'baked' && spots.length === 0 && (
          <g>
            <Sparkle x={COOKIE.cx - 20} y={COOKIE.cy - 16} r={3} />
            <Sparkle x={COOKIE.cx + 20} y={COOKIE.cy - 12} r={2.4} />
            <Sparkle x={COOKIE.cx + 4} y={COOKIE.cy - 24} r={2} />
          </g>
        )}
      </svg>
    </ToyShell>
  );
}
